import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { useToast } from "../../context/ToastContext";
import {
  Container,
  Paper,
  Group,
  Stack,
  Title,
  Text,
  Button,
  Avatar,
  Grid,
  Modal,
  Loader,
  ActionIcon,
  Center,
  Alert,
  Badge,
  Anchor,
  Divider,
} from "@mantine/core";
import {
  IconSnowflake,
  IconChevronLeft,
  IconTrendingUp,
  IconUser,
  IconArrowBack,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import { handleGenericError } from "../../utils/errorHandler";

// Note: ColdLeadsPage.css is no longer needed as we now use Mantine components

function ColdLeadsPage() {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();
  const [coldLeads, setColdLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch cold leads data
  const fetchColdLeads = async () => {
    setIsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/recruiting/list/cold"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setColdLeads(result.data || []);
      } else {
        showError("Failed to load cold leads");
      }
    } catch (error) {
      handleGenericError(error, "Error loading cold leads");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchColdLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToRecruiting = () => {
    navigate("/recruiting");
  };

  // Handle marking lead as not cold
  const handleMarkAsNotCold = lead => {
    setSelectedLead(lead);
    setShowConfirmModal(true);
  };

  // Confirm marking as not cold - using standard status endpoint
  const confirmMarkAsNotCold = async () => {
    if (!selectedLead) return;

    setIsUpdating(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/recruiting/leads/${selectedLead.id}/status`),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ status: "Lead" }),
        }
      );

      if (response.ok) {
        // Remove the lead from the cold leads list
        setColdLeads(prev => prev.filter(lead => lead.id !== selectedLead.id));
        setShowConfirmModal(false);
        setSelectedLead(null);
      } else {
        showError("Failed to update lead status");
      }
    } catch (error) {
      handleGenericError(error, "Error updating lead status");
    } finally {
      setIsUpdating(false);
    }
  };

  // Cancel confirmation
  const cancelMarkAsNotCold = () => {
    setShowConfirmModal(false);
    setSelectedLead(null);
  };

  return (
    <PageContent title="Cold Leads">
      <Container size="xl" px="md">
        {/* Page Header */}
        <Paper p="xl" mb="xl" withBorder>
          <Group justify="space-between" align="flex-start">
            <Group align="center">
              <Avatar size={48} radius="md" color="blue" variant="filled">
                <IconSnowflake size={24} />
              </Avatar>
              <Stack gap="xs">
                <Title order={1} size="h2">
                  Cold Leads
                </Title>
                <Text c="dimmed" size="sm" fw={500}>
                  {isLoading
                    ? "Loading..."
                    : `${coldLeads.length} ${coldLeads.length === 1 ? "lead" : "leads"} marked as cold`}
                </Text>
              </Stack>
            </Group>

            <Button
              leftSection={<IconChevronLeft size={16} />}
              onClick={handleBackToRecruiting}
              variant="filled"
            >
              Back to Recruiting
            </Button>
          </Group>
        </Paper>

        {/* Content */}
        {isLoading ? (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader color="blue" size="lg" />
              <Text c="dimmed">Loading cold leads...</Text>
            </Stack>
          </Center>
        ) : coldLeads.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="md" ta="center">
              <Avatar size={80} color="blue" variant="light">
                <IconSnowflake size={40} />
              </Avatar>
              <Title order={3}>No Cold Leads</Title>
              <Text c="dimmed" maw={400}>
                There are currently no leads marked as cold.
              </Text>
              <Button
                leftSection={<IconArrowBack size={16} />}
                onClick={handleBackToRecruiting}
                variant="filled"
              >
                Back to Recruiting Dashboard
              </Button>
            </Stack>
          </Center>
        ) : (
          <Grid gutter="lg">
            {coldLeads.map((lead, index) => (
              <Grid.Col
                key={lead.id || index}
                span={{ base: 12, sm: 6, lg: 4 }}
              >
                <Paper p="lg" withBorder radius="md" h="100%">
                  <Stack gap="md">
                    <Group justify="space-between" align="flex-start">
                      <Group align="center">
                        <Avatar
                          size={40}
                          radius="xl"
                          color="blue"
                          variant="filled"
                        >
                          <IconUser size={20} />
                        </Avatar>
                        <Stack gap={2}>
                          <Text fw={600} size="sm" lineClamp={1}>
                            {lead.name || `Unnamed Lead ${index + 1}`}
                          </Text>
                          <Badge size="xs" color="blue" variant="light">
                            Cold Lead
                          </Badge>
                        </Stack>
                      </Group>

                      <ActionIcon
                        variant="filled"
                        color="orange"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          handleMarkAsNotCold(lead);
                        }}
                        title="Mark as Not Cold"
                      >
                        <IconTrendingUp size={14} />
                      </ActionIcon>
                    </Group>

                    <Stack gap="xs">
                      {lead.email && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Email:
                          </Text>
                          <Anchor
                            href={`mailto:${lead.email}`}
                            size="xs"
                            lineClamp={1}
                          >
                            {lead.email}
                          </Anchor>
                        </Group>
                      )}

                      {(lead.phone || lead.phone_number) && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Phone:
                          </Text>
                          <Text size="xs" lineClamp={1}>
                            {lead.phone_number || lead.phone}
                          </Text>
                        </Group>
                      )}

                      {lead.instagram_username && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Instagram:
                          </Text>
                          <Anchor
                            href={`https://instagram.com/${lead.instagram_username}`}
                            target="_blank"
                            size="xs"
                            lineClamp={1}
                          >
                            @{lead.instagram_username}
                          </Anchor>
                        </Group>
                      )}

                      {lead.linkedin_profile && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            LinkedIn:
                          </Text>
                          <Anchor
                            href={lead.linkedin_profile}
                            target="_blank"
                            size="xs"
                            lineClamp={1}
                          >
                            View Profile
                          </Anchor>
                        </Group>
                      )}

                      {lead.recruiter_name && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Recruiter:
                          </Text>
                          <Text size="xs" lineClamp={1} fw={500}>
                            {lead.recruiter_name}
                          </Text>
                        </Group>
                      )}

                      {lead.last_contact_date && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Last contact:
                          </Text>
                          <Text size="xs" fs="italic">
                            {new Date(
                              lead.last_contact_date
                            ).toLocaleDateString()}
                          </Text>
                        </Group>
                      )}

                      {lead.last_meeting_date && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Last meeting:
                          </Text>
                          <Text size="xs" fs="italic">
                            {new Date(
                              lead.last_meeting_date
                            ).toLocaleDateString()}
                          </Text>
                        </Group>
                      )}
                    </Stack>

                    <Divider />

                    <Button
                      variant="light"
                      color="orange"
                      size="xs"
                      leftSection={<IconTrendingUp size={14} />}
                      onClick={e => {
                        e.stopPropagation();
                        handleMarkAsNotCold(lead);
                      }}
                      fullWidth
                    >
                      Mark as Not Cold
                    </Button>
                  </Stack>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        )}

        {/* Confirmation Modal */}
        <Modal
          opened={showConfirmModal}
          onClose={cancelMarkAsNotCold}
          title={
            <Group gap="sm">
              <IconTrendingUp size={20} />
              <Text fw={600}>Mark as Not Cold</Text>
            </Group>
          }
          size="md"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to mark{" "}
              <Text span fw={600}>
                {selectedLead?.name || "this lead"}
              </Text>{" "}
              as not cold?
            </Text>

            <Alert color="yellow" variant="light">
              <Text size="sm">
                This will remove them from the cold leads list and make them
                available for normal recruiting activities.
              </Text>
            </Alert>

            <Group justify="flex-end" gap="sm" mt="md">
              <Button
                variant="subtle"
                onClick={cancelMarkAsNotCold}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                color="orange"
                leftSection={
                  isUpdating ? (
                    <Loader size={16} color="white" />
                  ) : (
                    <IconTrendingUp size={16} />
                  )
                }
                onClick={confirmMarkAsNotCold}
                loading={isUpdating}
              >
                {isUpdating ? "Updating..." : "Mark as Not Cold"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </PageContent>
  );
}

export default ColdLeadsPage;
